import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Phone, Radio, X, Copy, Check } from 'lucide-react';
import { getToken } from '../lib/api';
import { useToast } from '../components/Toast';
import { deviceProfileFromModel, isDectFxsCandidate, isKnownModel, needsSerial } from '../lib/ataProfile';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { 'Content-Type': 'application/json', ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

const VENDORS = ['Grandstream', 'Snom', 'Yealink', 'Cisco', 'Other'];

type Line = { extension_uuid: string; label: string };
type Device = { id?: string; name: string; type: 'dect' | 'fxs'; vendor: string; model: string; mac: string; ip: string; serial?: string; lines: Line[]; prov_url?: string; device_uuid?: string; push_status?: string };

const empty = (): Device => ({ name: '', type: 'dect', vendor: 'Grandstream', model: '', mac: '', ip: '', serial: '', lines: [] });

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };
  return (
    <button className="btn-ghost p-1 ml-1" title="Copy provisioning URL" onClick={copy}>
      {copied ? <Check size={12} className="text-success" /> : <Copy size={12} className="text-textSecondary" />}
    </button>
  );
}

export default function DectFxs() {
  const qc = useQueryClient();
  const toast = useToast();
  const devices = useQuery<Device[]>({ queryKey: ['dect'], queryFn: () => fJson('/api/dect') });
  const exts = useQuery<any[]>({ queryKey: ['users'], queryFn: () => fJson('/api/users') });
  const [editing, setEditing] = useState<Device | null>(null);

  const save = useMutation({
    mutationFn: (d: Device) => d.id
      ? fJson(`/api/dect/${d.id}`, { method: 'PUT', body: JSON.stringify(d) })
      : fJson('/api/dect', { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dect'] }); setEditing(null); toast('success', 'Saved'); },
    onError: (e: any) => toast('error', e.message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => fJson(`/api/dect/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dect'] }); toast('success', 'Deleted'); },
    onError: (e: any) => toast('error', e.message),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">DECT / FXS Devices</h1>
        <button onClick={() => setEditing(empty())} className="btn-primary inline-flex items-center gap-2"><Plus size={14} /> Add Device</button>
      </div>
      <p className="text-sm text-textSecondary">Cordless DECT base stations and analog FXS gateways (ATAs). Bind each handset / analog port to an extension.</p>

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead><tr><th></th><th>Name</th><th>Type</th><th>Vendor / Model</th><th>MAC</th><th>Lines</th><th>Status</th><th>Provisioning URL</th><th></th></tr></thead>
          <tbody>
            {(devices.data || []).map(d => (
              <tr key={d.id} className="hover:bg-elevated cursor-pointer" onClick={() => setEditing({ ...empty(), ...d, lines: d.lines || [] })}>
                <td>{d.type === 'fxs' ? <Phone size={14} className="text-textSecondary" /> : <Radio size={14} className="text-textSecondary" />}</td>
                <td className="font-medium">{d.name}</td>
                <td className="uppercase text-xs">{d.type}</td>
                <td className="text-sm">{d.vendor} {d.model}</td>
                <td className="font-mono text-xs">{d.mac || '—'}</td>
                <td className="text-xs">{(d.lines || []).length}</td>
                <td className="text-xs font-mono">{d.push_status || '—'}</td>
                <td className="text-xs" onClick={e => e.stopPropagation()}>
                  {d.prov_url
                    ? <span className="font-mono text-textSecondary truncate max-w-xs inline-block align-bottom">{d.prov_url}<CopyButton text={d.prov_url} /></span>
                    : <span className="text-textSecondary">—</span>}
                </td>
                <td onClick={e => e.stopPropagation()}>
                  <button className="btn-ghost p-1 text-danger" onClick={() => d.id && confirm('Delete this device?') && remove.mutate(d.id)}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {!devices.data?.length && <tr><td colSpan={9} className="text-center py-8 text-textSecondary">No DECT/FXS devices yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && <DeviceForm device={editing} exts={exts.data || []} saving={save.isPending} onClose={() => setEditing(null)} onSave={(d: Device) => save.mutate(d)} />}
    </div>
  );
}

function DeviceForm({ device, exts, saving, onClose, onSave }: any) {
  const toast = useToast();
  const [d, setD] = useState<Device>(device);
  const set = (k: keyof Device, v: any) => setD({ ...d, [k]: v });
  const setLine = (i: number, k: keyof Line, v: any) => { const lines = [...d.lines]; lines[i] = { ...lines[i], [k]: v }; setD({ ...d, lines }); };
  const addLine = () => setD({ ...d, lines: [...d.lines, { extension_uuid: '', label: '' }] });
  const delLine = (i: number) => setD({ ...d, lines: d.lines.filter((_, j) => j !== i) });

  // Scan-driven add (#1156): reuse the SAME LAN discovery the PnP Phones page uses, so an
  // ATA/DECT base is picked from the scan instead of hand-typed. Only show the picker when
  // adding a NEW device (an existing device already has its identity).
  const discovered = useQuery<any[]>({ queryKey: ['discovered-dect'], queryFn: () => fJson('/api/phones/discovered'), enabled: !d.id });
  const scan = useMutation({
    mutationFn: () => fJson('/api/phones/active-scan', { method: 'POST', body: '{}' }),
    onSuccess: () => { toast('info', 'Scanning the LAN…'); setTimeout(() => discovered.refetch(), 4000); setTimeout(() => discovered.refetch(), 9000); },
    onError: (e: any) => toast('error', e.message),
  });
  const candidates = (discovered.data || []).filter(isDectFxsCandidate);
  const [pickedId, setPickedId] = useState<string>('');

  const macTail = (mac: string) => String(mac || '').replace(/[^0-9a-f]/gi, '').slice(-6).toUpperCase();
  const seedLines = (prof: { type: 'fxs' | 'dect'; ports: number }) =>
    Array.from({ length: prof.ports }, (_, i) => ({ extension_uuid: '', label: `${prof.type === 'fxs' ? 'Port' : 'Handset'} ${i + 1}` }));
  // Only re-seed port lines when the operator hasn't bound anything yet.
  const linesUnbound = (lines: Line[]) => !lines.length || lines.every(l => !l.extension_uuid);

  // Ask the device its exact model (same probe the PnP page's Identify uses), then re-apply
  // the profile so an HT802 gets its 2 ports. Grandstream often only reports the model on a
  // fresh boot; on failure the operator just types it.
  const identify = useMutation({
    mutationFn: (id: string) => fJson(`/api/phones/discovered/${id}/identify`, { method: 'POST', body: '{}' }),
    onSuccess: (r: any) => {
      if (r && isKnownModel(r.model)) {
        const prof = deviceProfileFromModel(r.model);
        setD(prev => ({
          ...prev,
          model: r.model,
          type: prof.type,
          name: (!prev.name || /\?$/.test(prev.name)) ? `${prof.type === 'fxs' ? 'ATA' : 'DECT'} ${r.model}` : prev.name,
          lines: linesUnbound(prev.lines) ? seedLines(prof) : prev.lines,
        }));
        toast('success', `Identified as ${r.model}`);
      } else {
        toast('info', 'Could not read the model — set it manually (the MAC/IP are filled in).');
      }
    },
    onError: () => toast('info', 'Could not reach the device to identify it — set the model manually.'),
  });

  // Pre-fill identity + the right number of port/handset lines from a discovered row.
  const applyDiscovered = (row: any) => {
    const model = isKnownModel(row.model) ? row.model : '';
    const prof = deviceProfileFromModel(model);
    const mac = row.mac_address || '';
    setD(prev => ({
      ...prev,
      vendor: 'Grandstream',
      model: model || (isKnownModel(prev.model) ? prev.model : ''),
      mac,
      ip: row.ip_address || prev.ip,
      type: prof.type,
      name: prev.name || `${prof.type === 'fxs' ? 'ATA' : 'DECT'} ${model || macTail(mac)}`,
      lines: prev.lines.length ? prev.lines : seedLines(prof),
    }));
    // Model unknown on the scan row → probe the device to resolve it (and its port count).
    if (!model && row.id) identify.mutate(String(row.id));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="card max-w-2xl w-full max-h-[90vh] overflow-auto p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">{d.id ? 'Edit' : 'Add'} DECT / FXS Device</h2>
          <button className="btn-ghost p-1" onClick={onClose}><X size={16} /></button>
        </div>

        {!d.id && (
          <div className="bg-elevated rounded p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">Pick a discovered device</div>
              <button type="button" className="btn-ghost text-xs inline-flex items-center gap-1"
                      disabled={scan.isPending} onClick={() => scan.mutate()}>
                <Radio size={12} /> {scan.isPending ? 'Scanning…' : 'Scan network'}
              </button>
            </div>
            <div className="flex gap-2">
              <select className="input flex-1" value={pickedId}
                      onChange={e => { setPickedId(e.target.value); const row = candidates.find((c: any) => String(c.id) === e.target.value); if (row) applyDiscovered(row); }}>
                <option value="">
                  {candidates.length ? `— Select from ${candidates.length} discovered Grandstream device(s) —` : 'No discovered devices yet — click Scan network'}
                </option>
                {candidates.map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {(isKnownModel(c.model) ? c.model : 'Unknown model')} · {c.ip_address || '—'} · {c.mac_address}
                  </option>
                ))}
              </select>
              {pickedId && !isKnownModel(d.model) && (
                <button type="button" className="btn-ghost text-xs whitespace-nowrap" disabled={identify.isPending}
                        onClick={() => identify.mutate(pickedId)}>
                  {identify.isPending ? 'Identifying…' : 'Identify model'}
                </button>
              )}
            </div>
            <div className="text-xs text-textSecondary">
              {identify.isPending
                ? 'Asking the device its model…'
                : 'Picking a device fills in the vendor, model, MAC, IP, and its ports below — then just bind each port to an extension. Or fill the fields in manually.'}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm">Name<input className="input mt-1" value={d.name} onChange={e => set('name', e.target.value)} placeholder="Front desk DECT" /></label>
          <label className="text-sm">Type
            <select className="input mt-1" value={d.type} onChange={e => set('type', e.target.value as any)}>
              <option value="dect">DECT (cordless)</option>
              <option value="fxs">FXS (analog ATA)</option>
            </select>
          </label>
          <label className="text-sm">Vendor
            <select className="input mt-1" value={d.vendor} onChange={e => set('vendor', e.target.value)}>{VENDORS.map(v => <option key={v}>{v}</option>)}</select>
          </label>
          <label className="text-sm">Model<input className="input mt-1" value={d.model} onChange={e => set('model', e.target.value)} placeholder="DP750 / HT812" /></label>
          <label className="text-sm">MAC<input className="input mt-1 font-mono" value={d.mac} onChange={e => set('mac', e.target.value)} placeholder="00:0b:82:xx:xx:xx" /></label>
          <label className="text-sm">IP<input className="input mt-1 font-mono" value={d.ip} onChange={e => set('ip', e.target.value)} placeholder="192.168.x.x" /></label>
          {/* SN is only needed for a DROP-SHIP device with no on-site SBC (GDMS RPS is then
              the only path, and GDMS hard-requires MAC+SN). A device we can already reach on
              the LAN provisions over multicast PnP — no GDMS, no SN. Hide the field so it isn't
              mistaken for required. */}
          {needsSerial(d) ? (
            <label className="text-sm col-span-2">Serial (SN) — drop-ship only
              <input className="input mt-1 font-mono" value={d.serial || ''} onChange={e => set('serial', e.target.value)} placeholder="From the box label — required for GDMS RPS zero-touch (no on-site SBC)" />
            </label>
          ) : (
            <div className="col-span-2 text-xs text-textSecondary bg-elevated rounded px-3 py-2">
              Local device on your LAN (<span className="font-mono">{d.ip}</span>) — provisions over multicast PnP. <span className="font-medium text-text">No serial number needed.</span> (SN is only required to drop-ship a device to a site with no on-site SBC.)
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="text-sm font-semibold">{d.type === 'fxs' ? 'Analog ports' : 'Handsets'} → extensions</div>
            <button className="btn-ghost text-sm inline-flex items-center gap-1" onClick={addLine}><Plus size={12} /> Add line</button>
          </div>
          <div className="space-y-2">
            {d.lines.map((ln, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input className="input w-40" value={ln.label} onChange={e => setLine(i, 'label', e.target.value)} placeholder={`${d.type === 'fxs' ? 'Port' : 'Handset'} ${i + 1}`} />
                <select className="input flex-1" value={ln.extension_uuid} onChange={e => setLine(i, 'extension_uuid', e.target.value)}>
                  <option value="">— Extension —</option>
                  {exts.map((u: any) => <option key={u.extension_uuid} value={u.extension_uuid}>{u.extension} — {u.name || ''}</option>)}
                </select>
                <button className="btn-ghost p-1 text-danger" onClick={() => delLine(i)}><Trash2 size={14} /></button>
              </div>
            ))}
            {!d.lines.length && <div className="text-xs text-textSecondary">No lines yet — add one to bind a handset/port to an extension.</div>}
          </div>
        </div>

        {d.prov_url && (
          <div className="bg-elevated rounded p-3 text-xs">
            <div className="text-textSecondary mb-1 font-semibold">Provisioning URL — paste into phone config server field</div>
            <div className="flex items-center gap-2">
              <span className="font-mono break-all">{d.prov_url}</span>
              <CopyButton text={d.prov_url} />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={saving || !d.name} onClick={() => onSave(d)}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
